import axios from "axios";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useAxiosHook } from "./useAxiosHook";

const ROUTE_PREFIX = "borrower_submissions";
// Submits are anonymous and live on their own router.
const PUBLIC_ROUTE_PREFIX = "public";
// The deal-generated application / credit-auth links live on the LO-portal
// router (loportal), NOT borrower_submissions (which is unmounted in the
// backend). See deal_generated_link/* below.
const LOPORTAL_PREFIX = "loportal";

/**
 * Borrower-facing submissions: the public loan application and credit
 * authorization, plus the internal read surface behind them.
 *
 * The application / credit-auth half of this hook is the tpo-portal-frontend
 * contract, ported verbatim — see lendmarq-os
 * `frontend_documentation/application-links-and-external-submissions.md` §3.3.
 * The legacy quick-app / credit-pull / assignment methods further down are
 * lendmarq-los-only and were left alone.
 *
 * The create* calls are reachable from unauthenticated routes — they post
 * anonymously, so nothing there may depend on a session.
 *
 * A link token is REQUIRED on the public surface: the submit identifies itself
 * with the `t` (or legacy `link_id`) the borrower arrived on, which is how the
 * submission gets attributed to the sending LO and how the funnel closes.
 *
 * Errors are real, not blanket 202s — 404 unknown link, 410 revoked/expired/
 * consumed, 413 upload limits, 422 field validation, 429 rate limited. Surface
 * them rather than showing a success screen.
 */
export const useBorrowerSubmissionsHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // -------------------------------------
  //  GET /borrower_submissions/get_applications
  // -------------------------------------
  const getApplications = async ({ onSuccessFn, onCompleteFn, onFailFn }) => {
    const endpoint = `${ROUTE_PREFIX}/get_applications`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Applications",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // -------------------------------------
  //  GET /borrower_submissions/get_credit_authorizations
  // -------------------------------------
  const getCreditAuthorizations = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_credit_authorizations`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Credit Authorizations",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // -------------------------------------
  //  GET /borrower_submissions/get_application/{app_id}
  // -------------------------------------
  const getApplicationById = async ({
    appId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Application ID is missing", { appId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/application/${appId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Application By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // -------------------------------------
  //  GET /borrower_submissions/get_credit_authorization/{app_id}
  // -------------------------------------
  const getCreditAuthorizationById = async ({
    appId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Credit Authorization ID is missing", { appId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/credit_authorization/${appId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Credit Authorization By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  /**
   * Shared body for both public submits — they take an identical multipart
   * contract and differ only in path.
   *
   * `t` / `link_id` / `consent_file_name` are their own form fields, not part of
   * `submission`: the server reads them off the form. The token is required, so
   * a submit with neither is a bug worth catching here rather than as a 404.
   */
  const submitPublicForm = async ({
    name,
    path,
    payload,
    token,
    linkId,
    consentFileName,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(payload) || isEmpty(payload)) {
      console.error(`${name}: submission data is missing`, { payload });
      return;
    }
    if (isNil(token) && isNil(linkId)) {
      console.error(`${name}: a link token is required on the public surface`);
    }
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name,
      path,
      payload,
      // Multipart: useAxiosHook splits `uploadMetadata` into `uploadFiles` parts
      // plus a single JSON `submission` part.
      formData: true,
      // The public routes read the part filename directly — `check_upload_limits`
      // 413s anything without an allowlisted extension, and `attach_uploads`
      // matches it against `consent_file_name`. Every OTHER (LOS) route matches
      // the part filename against the metadata `id` instead, which is the
      // default. This is the only surface that needs the real filename.
      usePublicFilenames: true,
      extraFormFields: {
        ...(token ? { t: token } : {}),
        ...(linkId ? { link_id: linkId } : {}),
        ...(consentFileName ? { consent_file_name: consentFileName } : {}),
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /public/application
  // -------------------------------------
  const createApplication = async (opts) =>
    submitPublicForm({
      ...opts,
      name: "Submit Application",
      path: `${PUBLIC_ROUTE_PREFIX}/application`,
    });

  const createQuickApplication = async ({
    // data,
    // files,
    payload,
    onSuccessFn,
  }) => {
    if (isNil(payload) || isEmpty(payload)) {
      console.error("Application data is missing", { payload });
      return;
    }
    const endpoint = `borrower_portal/submit_form`;
    // const endpoint = `${ROUTE_PREFIX}/borrower_portal/submit_form`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Quick Application",
      path: endpoint,
      cancelSource: source,
      payload,
      domain: "test",
      // errorTesting: true,
      formData: true,
      includeQueryParams: true,
      onSuccessFn,
      // errorTesting,
    });
    // await axiosPostRequest({
    //   name: "Create Application",
    //   path: endpoint,
    //   payload: formData,
    //   formData: true,
    //   cancelSource: source,
    //   onSuccessFn,
    //   onSuccessMsg: "Application created successfully",
    //   onCompleteFn,
    //   onFailFn,
    // });
  };

  const getQuickApplications = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    // const endpoint = "borrower_portal/forms";
    const endpoint = `${ROUTE_PREFIX}/quick_applications`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Quick Applications",
      path: endpoint,
      // type: "test",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  // 00000

  const createQuickApp = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Loan data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/quick_application`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      includeQueryParams: true,
      onSuccessMsg: "Quick App submitted successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const createQuickAppWithFileUploads = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Form data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/quick_application_with_upload`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan With File Uploads",
      path: endpoint,
      formData: true,
      payload: data,
      includeQueryParams: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Quick App submitted successfully",

      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/new_credit_authorization
  // -------------------------------------
  /**
   * Create a new credit authorization
   *
   * @example
   * // Example usage:
   * const authData = {
   *   firstName: "John",
   *   lastName: "Doe",
   *   emailAddress: "john.doe@example.com",
   *   phoneNumber: "555-123-4567",
   *   ssn: "123-45-6789",
   *   // Additional credit auth fields
   * };
   *
   * const files = [file1, file2]; // Array of file objects from input
   *
   * createCreditAuthorization({
   *   data: authData,
   *   files: files,
   *   onSuccessFn: (response) => console.log("Credit auth created:", response),
   *   onFailFn: (error) => console.error("Error creating credit auth:", error)
   * });
   */
  // -------------------------------------
  //  POST /public/credit_authorization
  // -------------------------------------
  const createCreditAuthorization = async (opts) =>
    submitPublicForm({
      ...opts,
      name: "Submit Credit Authorization",
      path: `${PUBLIC_ROUTE_PREFIX}/credit_authorization`,
    });

  // -------------------------------------
  //  POST /loportal/deal_generated_link/loan_application/{loanId}
  //  Body: { borrower } — the handler reads request_body['borrower'] and
  //  stores it as the link's `link_borrower` dict. Response: { link, link_id }.
  // -------------------------------------

  const generateDealApplicationLink = async ({
    loanId,
    borrowerData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isEmpty(loanId) || isEmpty(borrowerData)) {
      console.error("Deal ID is missing", { loanId });
      return;
    }
    const endpoint = `${LOPORTAL_PREFIX}/deal_generated_link/loan_application/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate Deal Application Link",
      path: endpoint,
      payload: { borrower: borrowerData },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Application link generated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /loportal/deal_generated_link/credit_authorization/{loanId} — same
  // body/response contract as the application link above.
  const generateDealCreditAuthLink = async ({
    loanId,
    borrowerData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isEmpty(loanId) || isEmpty(borrowerData)) {
      console.error("Deal ID is missing", { loanId });
      return;
    }
    const endpoint = `${LOPORTAL_PREFIX}/deal_generated_link/credit_authorization/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate Deal Credit Auth Link",
      path: endpoint,
      payload: { borrower: borrowerData },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Credit authorization link generated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const generateLOApplicationLink = async ({
    salesperson,
    linkTag,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/lo_generated_link/loan_application`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate LO Application Link",
      path: endpoint,
      payload: { salesperson, linkTag },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const generateLOCreditAuthLink = async ({
    linkTag,
    salesperson,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/lo_generated_link/credit_authorization`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate LO Credit Auth Link",
      path: endpoint,
      payload: { salesperson, linkTag },
      cancelSource: source,
      onSuccessFn,
      // onSuccessMsg: "Credit authorization link generated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const generateBorrowerApplicationLink = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Borrower data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/borrower_generated_link/loan_application`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate Borrower Application Link",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower application link generated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/borrower_generated_link/credit_authorization
  // -------------------------------------
  /**
   * Generate a credit authorization link for borrower website
   *
   * @example
   * // Example usage:
   * const borrowerInfo = {
   *   borrower: {
   *     emailAddress: "john.doe@example.com",
   *     firstName: "John",
   *     lastName: "Doe",
   *     phoneNumber: "555-123-4567"
   *   }
   * };
   *
   * generateBorrowerCreditAuthLink({
   *   data: borrowerInfo,
   *   onSuccessFn: (response) => console.log("Link generated:", response),
   *   onFailFn: (error) => console.error("Error generating link:", error)
   * });
   */
  const generateBorrowerCreditAuthLink = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Borrower data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/borrower_generated_link/credit_authorization`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Generate Borrower Credit Auth Link",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower credit auth link generated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/assign_app_to_deal/{app_id}/{deal_id}
  // -------------------------------------
  /**
   * Assign an application to a deal
   *
   * @example
   * // Example usage:
   * const assignData = {
   *   salesperson: "sales@example.com" // Optional
   * };
   *
   * assignApplicationToDeal({
   *   appId: "60a12345b789c0d123e45f67",
   *   loanId: "60b67890c1d2e3f456g78h90",
   *   data: assignData,
   *   onSuccessFn: (response) => console.log("Application assigned:", response)
   * });
   */
  const assignApplicationToDeal = async ({
    appId,
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Application ID is missing", { appId });
      return;
    }
    if (isNil(loanId) || isEmpty(loanId)) {
      console.error("Deal ID is missing", { loanId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/assign_app_to_deal/${appId}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Assign Application to Deal",
      path: endpoint,
      payload: data || {},
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: "Application assigned to deal successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const assignAuthToDeal = async ({
    appId,
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Credit Authorization ID is missing", { appId });
      return;
    }
    if (isNil(loanId) || isEmpty(loanId)) {
      console.error("Deal ID is missing", { loanId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/assign_auth_to_deal/${appId}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Assign Credit Auth to Deal",
      path: endpoint,
      payload: data || {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Credit authorization assigned to deal successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/assign_app_to_salesperson/{app_id}
  // -------------------------------------
  /**
   * Assign an application to a salesperson
   *
   * @example
   * // Example usage:
   * const salespersonData = {
   *   salesperson: "sales@example.com"
   * };
   *
   * assignApplicationToSalesperson({
   *   appId: "60a12345b789c0d123e45f67",
   *   data: salespersonData,
   *   onSuccessFn: (response) => console.log("Application assigned:", response)
   * });
   */
  const assignApplicationToSalesperson = async ({
    appId,
    emailAddress,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Application ID is missing", { appId });
      return;
    }
    if (isNil(emailAddress)) {
      console.error("Salesperson email is missing", emailAddress);
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/assign_app_to_salesperson/${appId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Assign Application to Salesperson",
      path: endpoint,
      payload: { salesperson: emailAddress },
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: "Application assigned to salesperson successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/assign_auth_to_salesperson/{app_id}
  // -------------------------------------
  /**
   * Assign a credit authorization to a salesperson
   *
   * @example
   * // Example usage:
   * const salespersonData = {
   *   salesperson: "sales@example.com"
   * };
   *
   * assignAuthToSalesperson({
   *   appId: "60a12345b789c0d123e45f67",
   *   data: salespersonData,
   *   onSuccessFn: (response) => console.log("Credit auth assigned:", response)
   * });
   */
  const assignAuthToSalesperson = async ({
    appId,
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(appId) || isEmpty(appId)) {
      console.error("Application ID is missing", { appId });
      return;
    }
    if (isNil(emailAddress)) {
      console.error("Salesperson email is missing", emailAddress);
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/assign_auth_to_salesperson/${appId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Assign Credit Auth to Salesperson",
      path: endpoint,
      payload: { salesperson: emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Credit authorization assigned to salesperson successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const pullCreditReportBorrowerPayload = async ({
    creditAuthId,
    payload,
    onSuccessFn,
    fileDownloadName,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(creditAuthId) || isEmpty(creditAuthId)) {
      console.error("Credit Authorization ID is missing", { creditAuthId });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/pull_credit_report_borrower_payload/${creditAuthId}`;
    const source = axios.CancelToken.source();

    // console.log("payload", payload);
    // return;
    await axiosPostRequest({
      name: "Pull Credit Report Borrower Payload",
      path: endpoint,
      payload: payload,
      cancelSource: source,
      isFile: true, // This tells axios to expect a file response
      fileDownload: true, // This triggers automatic download
      fileDownloadName:
        fileDownloadName ||
        `credit_report_${creditAuthId}_${new Date().toISOString().split("T")[0]}.pdf`,
      responseType: "blob", // Ensure we get binary data
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // const pullCreditReportBorrowerPayload = async ({
  //   creditAuthId,
  //   payload,

  //   onSuccessFn,
  //   onCompleteFn,
  //   onFailFn,
  // }) => {
  //   if (isNil(creditAuthId) || isEmpty(creditAuthId)) {
  //     console.error("Credit Authorization ID is missing", { creditAuthId });
  //     return;
  //   }

  //   const endpoint = `${ROUTE_PREFIX}/pull_credit_report_borrower_payload/${creditAuthId}`;
  //   const source = axios.CancelToken.source();

  //   await axiosPostRequest({
  //     name: "Pull Credit Report Borrower Payload",
  //     path: endpoint,
  //     payload: payload,
  //     cancelSource: source,
  //     onSuccessFn,
  //     // errorTesting: true,
  //     // onSuccessMsg: "Credit report borrower payload pulled successfully",
  //     onCompleteFn,
  //     onFailFn,
  //   });
  // };

  const getBorrowerPortalUsers = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/signed_up_users/all`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Applications",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    loading,
    error,
    createQuickApplication,
    getApplications,
    getCreditAuthorizations,
    getApplicationById,
    getCreditAuthorizationById,
    createApplication,
    createCreditAuthorization,
    generateDealApplicationLink,
    generateDealCreditAuthLink,
    generateLOApplicationLink,
    generateLOCreditAuthLink,
    generateBorrowerApplicationLink,
    generateBorrowerCreditAuthLink,
    assignApplicationToDeal,
    assignAuthToDeal,
    getQuickApplications,
    assignApplicationToSalesperson,
    assignAuthToSalesperson,
    pullCreditReportBorrowerPayload,

    createQuickApp,
    createQuickAppWithFileUploads,
    getBorrowerPortalUsers,
  };
};
